// La page de réponse à une invitation, sans compte (D-147, maquettes r1 validées) : /l/<jeton>, au vouvoiement, aux
// couleurs du club. Le bouton de l'e-mail l'ouvre avec ?r=oui ou ?r=non : c'est LA PAGE, dans le navigateur, qui
// enregistre la réponse (jamais la simple ouverture du lien : un antivirus de messagerie qui l'ouvre tout seul, ou un
// navigateur automatisé, ne répond pas — la page leur montre les deux boutons). « Je viens » : le match, l'agenda, ce
// que le club offre, le bon du soir du match (à rouvrir le jour J, même sans compte). « Je ne peux pas » : un mot pour le
// club, facultatif. La réponse se change jusqu'au coup d'envoi. /l/<jeton>?stop=1 : « Ne plus recevoir ces messages ».
import './lien.css';
import { lienLink, lienLinkAnswer, lienLinkBon, lienLinkSeen, lienLinkStop, type LinkClub, type LinkInvitation, type LinkView } from '../lib/lien-api';
import { giftLines, greeting } from '../../supabase/functions/_shared/partner-mail';
import { bonTitle, longDay, shortDay } from '../domain/lien';
import type { Ctx } from './context';
import { $, html, mount, toast, type Html } from './dom';
import { openUseHold } from './perks';
import { downloadIcs } from './site-render';
import { applyBrand } from './theme';

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const COMPETITION: Record<string, string> = { championnat: 'Championnat', coupe: 'Coupe', phase_finale: 'Phase finale', brassage: 'Brassage', amical: 'Match amical' };
/** Un vrai navigateur, une personne devant (pas un navigateur automatisé, ni un onglet caché ou sans la main) : lui seul
 *  enregistre la réponse du bouton de l'e-mail. Sinon la page montre les deux boutons et attend. */
const automated = () => !!(navigator as Navigator & { webdriver?: boolean }).webdriver;
const humanNow = () => document.visibilityState === 'visible' && document.hasFocus();
const whenHuman = () => new Promise<void>(res => {
  if (humanNow()) { res(); return; }
  const evs = ['visibilitychange', 'focus', 'pointerdown', 'keydown', 'touchstart'];
  const f = () => { if (!humanNow()) return; for (const e of evs) window.removeEventListener(e, f, true); res(); };
  for (const e of evs) window.addEventListener(e, f, true);
});

export async function renderLienAnswer(root: HTMLElement, token: string, params: URLSearchParams): Promise<void> {
  const robots = document.createElement('meta');
  robots.name = 'robots'; robots.content = 'noindex, nofollow';
  document.head.append(robots);
  let v: LinkView | null = null;
  try { v = await lienLink(token); } catch { /* même message */ }
  if (!v) {
    document.title = 'Lien introuvable';
    mount(root, html`<main class="pub-missing"><h1>Lien introuvable</h1>
      <p>Vérifiez le lien reçu, ou contactez directement le club qui vous l’a envoyé.</p></main>`);
    return;
  }
  const view = v;
  const club = view.club;
  applyBrand({ id: `lien:${token.slice(0, 8)}`, name: club.name, short_name: club.short_name, logo_url: club.logo, primary_color: club.color }, false);
  document.title = `${club.name} · ${view.kind === 'invitation' ? 'Invitation au match' : 'Vos préférences'}`;
  const hello = greeting(view.partner.civility, view.partner.contact);
  const page = (sub: string, body: Html) => { mount(root, shell(club, sub, body)); window.scrollTo(0, 0); };

  // --- Ne plus recevoir ces messages -----------------------------------------------------------------------
  if (params.has('stop') || view.kind === 'mail') {
    const done = view.partner.stopped;
    page('Vos préférences', done ? html`<section class="pub-thanks"><p class="pub-kicker">✓ C’est noté</p><h3>Plus aucun e-mail</h3>
        <p>Le ${club.name} ne vous écrit plus par e-mail. Ses messages restent dans votre espace partenaire, si vous en avez un.</p></section>`
      : html`<section class="pub-sec pm-stop"><h2>Ne plus recevoir ces messages</h2>
        <p>Le ${club.name} ne vous écrira plus par e-mail${view.partner.email_hint ? html` (<strong>${view.partner.email_hint}</strong>)` : ''} : ni invitation, ni nouvelles.
          Ses messages restent dans votre espace partenaire, si vous en avez un.</p>
        <button type="button" class="btn btn-primary" id="pm-stop">Confirmer</button>
        <p class="hint">Vous pourrez les recevoir de nouveau depuis votre espace partenaire, ou en le demandant au club.</p></section>`);
    $(root, '#pm-stop')?.addEventListener('click', async e => {
      const b = e.currentTarget as HTMLButtonElement;
      b.disabled = true;
      const hint = await lienLinkStop(token).catch(() => null);
      if (hint === null) { b.disabled = false; b.textContent = 'Réessayer'; return; }
      page('Vos préférences', html`<section class="pub-thanks"><p class="pub-kicker">✓ C’est noté</p><h3>Plus aucun e-mail</h3>
        <p>Le ${club.name} ne vous écrira plus par e-mail${hint ? ` (${hint})` : ''}.</p></section>`);
    });
    return;
  }

  const inv0 = view.invitation;
  // L'ouverture est notée par le navigateur, une personne devant (« ouverte ce matin » pour le bureau).
  if (!automated()) void whenHuman().then(() => lienLinkSeen(token)).catch(() => undefined);
  if (inv0.cancelled) {
    page('Invitation au match', html`<section class="pub-thanks"><p class="pub-kicker">Invitation annulée</p><h3>Plus d’actualité</h3>
      <p>Le club a annulé cette invitation au match ${inv0.title}. Rien à faire de votre côté.</p></section>`);
    return;
  }
  const want = params.get('r') === 'oui' ? true : params.get('r') === 'non' ? false : null;
  if (want !== null) history.replaceState(null, '', location.pathname);   // recharger la page ne répond pas une seconde fois
  const pending = want !== null && inv0.open && (want ? 'vient' : 'pas_cette_fois') !== inv0.status && !automated();
  const record = async () => { try { await lienLinkAnswer(token, want!); } catch { /* la page dit l'état réel */ } await show(); };
  // (la page se dessine en fin de fonction : les aides plus bas sont alors prêtes)

  async function show(): Promise<void> {
    const fresh = await lienLink(token).catch(() => null);
    if (!fresh || fresh.kind !== 'invitation') return;
    const inv = fresh.invitation;
    if (!inv.open) { page('Invitation au match', closed(inv)); wireYes(inv); return; }
    if (inv.status === 'vient') return yes(inv);
    if (inv.status === 'pas_cette_fois') return no(inv);
    return ask(inv);
  }
  const answer = async (yes: boolean, note: string | null = null) => {
    try { await lienLinkAnswer(token, yes, note); }
    catch (e) { toast(String((e as Error).message).includes('close') ? 'Le match a commencé : la réponse est close.' : 'Réponse impossible pour le moment. Réessayez.', 'ko'); }
    await show();
  };

  function matchCard(inv: LinkInvitation, withHost: boolean): Html {
    return html`<section class="card pm-ans-card"><p class="pub-kicker">Le match</p><strong>${inv.title}</strong>
      <span class="muted small">${cap(longDay(inv.day))} · ${inv.time}${inv.location ? ` · ${inv.location}` : ''}</span>
      ${withHost && inv.host_name ? html`<span class="small">${inv.host_name} vous attendra à l’entrée.</span>` : ''}
      ${withHost ? html`<button type="button" class="btn btn-ghost btn-sm" id="pm-ics">Ajouter à mon agenda</button>` : ''}</section>`;
  }
  const offered = (inv: LinkInvitation) => giftLines(inv.gifts ?? [], inv.thanks_mic, view.partner.name);
  function bonCard(inv: LinkInvitation): Html {
    const title = bonTitle(inv.gifts ?? []);
    if (!title) return html``;
    return html`<div class="ln-pass"><p class="eyebrow">Le soir du match · à la buvette</p><span class="ln-big">${title}</span><span>Un bon offert par le club</span>
      ${inv.bon_used_at ? html`<span class="ln-ring ln-used">Utilisé<br><small>code ${inv.bon_code}</small></span>`
        : html`<button type="button" class="ln-ring" id="pm-bon" ${inv.today ? '' : 'disabled'}>${inv.today ? 'Appuyez 5 s' : cap(shortDay(inv.day))}</button>`}
      <span class="small">${inv.bon_used_at ? 'Bonne soirée !' : inv.today ? 'Montrez l’écran à la buvette : un code et l’heure qui tourne.'
        : 'Rouvrez ce lien le soir du match : un appui de 5 s, à montrer à la buvette.'}</span></div>`;
  }
  function wireYes(inv: LinkInvitation) {
    $(root, '#pm-ics')?.addEventListener('click', () => downloadIcs(`${inv.title} · ${club.name}`, inv.starts_at, inv.location ?? ''));
    $(root, '#pm-bon')?.addEventListener('click', () => {
      // Sans compte : l'appui de 5 s des avantages, au vouvoiement, aux couleurs du club.
      const pseudo = { club: { id: 'lien', name: club.name, short_name: club.short_name, logo_url: club.logo, primary_color: club.color },
        partners: [{}], full: { people: [], adminOf: [], coachOf: new Set() } } as unknown as Ctx;
      openUseHold(pseudo, { title: bonTitle(inv.gifts), picto: 'cadeau', details: `${inv.title} · offert par le club` }, () => lienLinkBon(token), () => void show());
    });
  }
  function yes(inv: LinkInvitation) {
    const gifts = offered(inv);
    page('Invitation au match', html`
      <section class="pub-thanks"><p class="pub-kicker">✓ C’est noté</p><h3>Vous venez !</h3><p>Merci${hello ? ` ${hello}` : ''} : le club est prévenu.</p></section>
      ${matchCard(inv, true)}
      ${gifts.length ? html`<section class="pub-sec"><h2>Le club vous offre</h2><ul class="po-ul">${gifts.map(g => html`<li>${g}</li>`)}</ul></section>` : ''}
      ${bonCard(inv)}
      <p class="pm-change">Un empêchement ? <button type="button" class="pm-inline" id="pm-no">Je ne peux plus venir</button> · jusqu’au coup d’envoi.</p>`);
    wireYes(inv);
    $(root, '#pm-no')!.addEventListener('click', () => void answer(false));
  }
  function no(inv: LinkInvitation) {
    page('Invitation au match', html`
      <section class="pub-thanks"><p class="pub-kicker">✓ C’est noté</p><h3>Dommage !</h3><p>Le club est prévenu. À une prochaine fois${hello ? `, ${hello}` : ''}.</p></section>
      ${matchCard(inv, false)}
      <section class="pub-sec"><h2>Un mot pour le club ?</h2>
        <form class="form" id="pm-word" novalidate><label class="field"><span>Facultatif</span><textarea name="note" rows="3" maxlength="300">${inv.answer_note ?? ''}</textarea></label>
          <button type="submit" class="btn btn-primary">${inv.answer_note ? 'Modifier mon mot' : 'Envoyer'}</button></form></section>
      <p class="pm-change">Finalement libre ? <button type="button" class="pm-inline" id="pm-yes">Je viens</button> · jusqu’au coup d’envoi.</p>`);
    $(root, '#pm-yes')!.addEventListener('click', () => void answer(true));
    const f = $<HTMLFormElement>(root, '#pm-word')!;
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const note = String(new FormData(f).get('note') ?? '').trim();
      const b = $<HTMLButtonElement>(f, 'button')!;
      b.disabled = true;
      try { await lienLinkAnswer(token, false, note); toast(note ? 'Merci : votre mot est envoyé au club' : 'C’est noté'); await show(); }
      catch { b.disabled = false; toast('Envoi impossible pour le moment. Réessayez.', 'ko'); }
    });
  }
  function ask(inv: LinkInvitation) {
    const gifts = offered(inv);
    page('Invitation au match', html`
      <section class="pub-hero"><p class="pub-kicker">${['Match à domicile', inv.competition ? COMPETITION[inv.competition] ?? '' : ''].filter(Boolean).join(' · ')}</p><h2>${inv.title}</h2>
        <p class="pub-when">${cap(longDay(inv.day))} · ${inv.time}${inv.location ? ` · ${inv.location}` : ''}</p></section>
      <section class="pub-thanks"><h3>Le club vous invite</h3><p>Bonjour${hello ? ` ${hello}` : ''}, nous serions ravis de vous accueillir${inv.host_name ? ` : ${inv.host_name} vous attendra à l’entrée` : ''}.</p></section>
      ${gifts.length ? html`<section class="pub-sec"><h2>Le club vous offre</h2><ul class="po-ul">${gifts.map(g => html`<li>${g}</li>`)}</ul></section>` : ''}
      <section class="pub-sec"><h2>Pouvez-vous nous dire si vous venez ?</h2>
        <div class="pm-two"><button type="button" class="btn btn-primary" id="pm-yes">Je viens</button><button type="button" class="btn btn-ghost" id="pm-no">Je ne peux pas</button></div>
        <p class="hint">Un clic suffit, sans compte. Vous pourrez changer d’avis jusqu’au coup d’envoi.</p></section>`);
    $(root, '#pm-yes')!.addEventListener('click', () => void answer(true));
    $(root, '#pm-no')!.addEventListener('click', () => void answer(false));
  }
  function closed(inv: LinkInvitation): Html {
    const tonight = inv.status === 'vient' && inv.today;
    return html`<section class="pub-thanks"><p class="pub-kicker">${tonight ? 'Ce soir' : 'Le match a commencé'}</p><h3>${tonight ? 'Bon match !' : 'Merci !'}</h3>
      <p>${tonight ? `Merci${hello ? ` ${hello}` : ''} : le club est heureux de vous accueillir.` : 'La réponse à cette invitation est close.'}</p></section>
      ${tonight ? bonCard(inv) : ''}`;
  }
  if (pending && humanNow()) await record();
  else {
    await show();
    // Personne devant l'écran pour l'instant (onglet caché, sans la main) : les deux boutons, et la réponse du bouton de
    // l'e-mail quand on la regarde.
    if (pending) void whenHuman().then(record);
  }
}

function shell(club: LinkClub, sub: string, body: Html): Html {
  const logo = club.logo ? html`<img class="pub-logo" src="${club.logo}" alt="Logo du ${club.name}">`
    : html`<span class="pub-logo pub-mono" aria-hidden="true">${(club.short_name || club.name).slice(0, 3).toUpperCase()}</span>`;
  return html`<div class="pub dp-pub">
    <header class="pub-head"><div class="pub-id">${logo}<div><h1>${club.name}</h1><p>${sub}</p></div></div></header>
    <main class="pub-main">${body}</main>
    <footer class="pub-foot">${logo}<div><strong>${club.name}</strong><small>Réponse sans compte · propulsé par Arceau</small></div></footer>
  </div>`;
}
